/**
 * Client slot picker (AD-04).
 *
 * Shows the open slots offered to the client's service group, each labelled
 * with the therapist it belongs to — photo, name and pronouns. That is
 * deliberate: choosing a slot is how a client expresses a therapist
 * preference (D-14), which is why there is no separate preference question
 * anywhere in signup.
 *
 * Claiming a slot also assigns the client to that therapist (D-15), so the
 * confirmation copy says so plainly rather than treating it as a booking
 * detail.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Button, Avatar, Chip, Alert,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, Divider
} from '@mui/material';
import { Event as EventIcon, CheckCircle as CheckIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getAvailableSlots, claimSlot, releaseSlot } from '../../services/availabilityApi';
import api from '../../services/api';

// An appointment is only 'upcoming' while it is still open: a completed or
// missed call must leave this list and close its waiting room (ST-20260916-0002).
const OPEN_APPOINTMENT_STATUSES = ['scheduled', 'confirmed'];

const dayKey = (iso) =>
  new Date(iso).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });

const timeLabel = (iso) =>
  new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

export default function BookAppointment({ token, slotType = 'intake', onBooked }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(null);
  const [booking, setBooking] = useState(false);
  const [booked, setBooked] = useState(null);
  // AD-48: steps the server says must come before an intake booking.
  const [blocked, setBlocked] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await getAvailableSlots({ slotType }, token);
      setSlots(res?.slots || []);
      setBlocked(res?.bookingBlocked || null);

      // If they already hold a booking of this type, show it rather than an
      // empty picker — arriving here from "view or change your appointment"
      // must not look like the booking vanished.
      try {
        const mine = await api.get('/appointments/me', token);
        const existing = (mine.data?.appointments || [])
          .filter(a => OPEN_APPOINTMENT_STATUSES.includes(a.status) && a.slotId && new Date(a.scheduledStart) > new Date())
          .sort((a, b) => new Date(a.scheduledStart) - new Date(b.scheduledStart))[0];
        if (existing) {
          const t = existing.therapistId || {};
          setBooked({
            slot: { _id: existing.slotId },
            appointment: existing,
            startsAt: existing.scheduledStart,
            therapist: {
              displayName: t.therapistInfo?.displayName
                || [t.firstName, t.lastName].filter(Boolean).join(' ') || t.name || 'Your therapist',
              pronouns: t.therapistInfo?.pronouns || '',
              profilePictureUrl: t.therapistInfo?.profilePictureUrl || ''
            }
          });
        }
      } catch (_) { /* fall through to the picker */ }
    } catch (err) {
      console.error('Error loading slots:', err);
      setError(err.response?.data?.msg || 'Could not load available appointments');
    } finally {
      setLoading(false);
    }
  }, [token, slotType]);

  useEffect(() => { load(); }, [load]);

  const handleConfirm = async () => {
    if (!confirming) return;
    try {
      setBooking(true);
      setError('');
      const res = await claimSlot(confirming._id, token);
      // The picker may have withheld the therapist's identity (D-14), but the
      // claim response always carries it — booking is the assignment.
      setBooked({
        ...res,
        therapist: res?.slot?.therapist || confirming.therapist,
        startsAt: confirming.startsAt
      });
      setConfirming(null);
      if (onBooked) onBooked(res);
    } catch (err) {
      console.error('Error claiming slot:', err);
      // A 409 here is the normal race outcome, not a fault — someone else took
      // the slot between rendering and confirming. Refresh so the list is true.
      if (err.response?.data?.code === 'booking_requires_intake') {
        setBlocked({ outstanding: err.response.data.outstanding || [] });
      }
      setError(err.response?.data?.msg || 'Could not book that appointment');
      setConfirming(null);
      await load();
    } finally {
      setBooking(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!booked?.slot?._id) return;
    try {
      setBooking(true);
      await releaseSlot(booked.slot._id, token);
      setBooked(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.msg || 'Could not cancel that appointment');
    } finally {
      setBooking(false);
    }
  };

  // Group by day so the list reads like a calendar rather than a flat feed.
  const byDay = useMemo(() => {
    const map = new Map();
    for (const s of slots) {
      const k = dayKey(s.startsAt);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(s);
    }
    return [...map.entries()];
  }, [slots]);

  if (booked) {
    return (
      <Card>
        <CardContent>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
            <CheckIcon color="success" />
            <Typography variant="h6">Your appointment is booked</Typography>
          </Stack>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Avatar src={booked.therapist?.profilePictureUrl || undefined} sx={{ width: 56, height: 56 }}>
              {(booked.therapist?.displayName || '?').charAt(0)}
            </Avatar>
            <Box>
              <Typography variant="subtitle1">
                {booked.therapist?.displayName || 'Your therapist'}
              </Typography>
              {booked.therapist?.pronouns && (
                <Typography variant="body2" color="text.secondary">{booked.therapist.pronouns}</Typography>
              )}
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {dayKey(booked.startsAt)} at {timeLabel(booked.startsAt)}
              </Typography>
            </Box>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {booked.therapist?.displayName || 'Your therapist'} will be your therapist. You will get a
            reminder before the appointment, and a link to join the video call.
          </Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {/* The confirmation was a dead end: cancelling was the only action, so
              the only way onwards was the browser's back button. */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="contained" onClick={() => navigate('/portal')}>
              Back to your portal
            </Button>
            <Button size="small" color="error" onClick={handleCancelBooking} disabled={booking}>
              {booking ? 'Cancelling…' : 'Cancel this appointment'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <EventIcon color="primary" />
          <Typography variant="h6">
            {slotType === 'intake' ? 'Book your first appointment' : 'Book your final appointment'}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {slots.some(s => s.showsTherapist === false)
            ? 'Choose a time that suits you. You will see which therapist you are meeting as soon as you book.'
            : 'Choose a time that suits you. The therapist shown with each time is who you will meet, and who will support you from then on.'}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {loading && <CircularProgress size={22} />}

        {/* AD-48: booking opens once consent and the questionnaires are done.
            Show what is left, with a way to each step, not an empty list. */}
        {!loading && blocked && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Booking opens once the steps below are done. Your therapist reads your questionnaires
              before your first appointment, so the time is spent on you.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              {(blocked.outstanding || []).map(step => (
                <Button key={step.key} variant="contained" size="small" onClick={() => navigate(step.to)}>
                  {step.label}
                </Button>
              ))}
            </Stack>
          </Alert>
        )}

        {!loading && !blocked && !slots.length && (
          <Alert severity="info">
            There are no appointments available to book right now. Please check back shortly — new
            times are added regularly.
          </Alert>
        )}

        <Stack spacing={2}>
          {byDay.map(([day, daySlots]) => (
            <Box key={day}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>{day}</Typography>
              <Stack spacing={1}>
                {daySlots.map(s => (
                  <Stack
                    key={s._id}
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                  >
                    {/* When the service group does not name therapists in the
                        picker, this is a list of times — no avatar, no gap
                        where a face used to be. The therapist is shown once the
                        slot is claimed. */}
                    {s.showsTherapist !== false && (
                      <Avatar src={s.therapist?.profilePictureUrl || undefined} sx={{ width: 44, height: 44 }}>
                        {(s.therapist?.displayName || '?').charAt(0)}
                      </Avatar>
                    )}
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="body1">{timeLabel(s.startsAt)}</Typography>
                      {s.showsTherapist !== false ? (
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {s.therapist?.displayName}
                          {s.therapist?.pronouns ? ` · ${s.therapist.pronouns}` : ''}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {(s.endsAt && s.startsAt)
                            ? `${Math.round((new Date(s.endsAt) - new Date(s.startsAt)) / 60000)} minutes`
                            : 'Appointment'}
                        </Typography>
                      )}
                    </Box>
                    <Chip size="small" variant="outlined" label={s.modality === 'in_person' ? 'In person' : 'Video'} />
                    <Button variant="contained" size="small" onClick={() => setConfirming(s)}>
                      Choose
                    </Button>
                  </Stack>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      </CardContent>

      <Dialog open={!!confirming} onClose={() => setConfirming(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm your appointment</DialogTitle>
        <DialogContent>
          {confirming && confirming.showsTherapist === false && (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                {dayKey(confirming.startsAt)} at {timeLabel(confirming.startsAt)}.
              </DialogContentText>
              <DialogContentText>
                This first appointment is with one of our therapists, who will talk with you about what
                you are looking for. You will see who you are meeting as soon as you book, and they will
                be your therapist for the programme.
              </DialogContentText>
            </>
          )}
          {confirming && confirming.showsTherapist !== false && (
            <>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Avatar src={confirming.therapist?.profilePictureUrl || undefined} sx={{ width: 48, height: 48 }}>
                  {(confirming.therapist?.displayName || '?').charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1">{confirming.therapist?.displayName}</Typography>
                  {confirming.therapist?.pronouns && (
                    <Typography variant="body2" color="text.secondary">{confirming.therapist.pronouns}</Typography>
                  )}
                </Box>
              </Stack>
              {confirming.therapist?.shortBio && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {confirming.therapist.shortBio}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </>
              )}
              <DialogContentText>
                {dayKey(confirming.startsAt)} at {timeLabel(confirming.startsAt)}.
                {' '}Booking this appointment means {confirming.therapist?.displayName} will be your therapist.
              </DialogContentText>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)} disabled={booking}>Back</Button>
          <Button variant="contained" onClick={handleConfirm} disabled={booking}>
            {booking ? 'Booking…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
